import { useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import { ErrorTypography } from "components/ErrorTypography";
import {
  useDisclosure,
  useRoleContext,
  useShipperShipmentNotes,
  useShipperShipmentPageContext,
} from "hooks";
import { ISOStringToLocalReadableTimestamp } from "utils";

import { CreateShipmentNoteModal } from "./CreateShipmentNoteModal";
import { DeleteShipmentNoteModal } from "./DeleteShipmentNoteModal";
import { getDeleteNoteAriaLabel } from "./utils";
import { EVOTRUX_ERROR_RED } from "../../../../../../constants";

interface Props {
  dividerComponent: JSX.Element;
}

export function ShipmentPrivateNotesSection(props: Props) {
  const { dividerComponent } = props;
  const { shipment } = useShipperShipmentPageContext();
  const { role } = useRoleContext();
  const {
    isOpen: isModalOpen,
    onOpen: onOpenModal,
    onClose: onCloseModal,
  } = useDisclosure(false);

  const [noteToDelete, setNoteToDelete] = useState<ShipperShipmentNote | null>(
    null,
  );

  const { notes, loading, error, refetch } = useShipperShipmentNotes({
    shipmentId: shipment?.info?.id,
  });

  if (!shipment || role?.type !== "SHIPPER") {
    return null;
  }

  const handleNoteCreated = () => {
    refetch();
    onCloseModal();
  };

  return (
    <>
      {dividerComponent}
      <Stack direction="column" spacing={2}>
        {loading && (
          <Stack alignItems="center" py={2}>
            <CircularProgress size={24} />
          </Stack>
        )}

        <ErrorTypography error={error} />

        {!loading && notes.length === 0 && !error && (
          <Typography variant="body2" color="text.secondary">
            No notes yet.
          </Typography>
        )}

        {!loading && notes.length > 0 && (
          <Stack direction="column" spacing={2}>
            {notes.map((note) => (
              <Stack
                key={note.id}
                direction="row"
                alignItems="flex-start"
                spacing={2}
                sx={{
                  p: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                <Stack direction="column" sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body1"
                    sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                  >
                    {note.text}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    {`${note.created_by.first_name} ${note.created_by.last_name}`}
                    {" • "}
                    {ISOStringToLocalReadableTimestamp(note.created_at)}
                  </Typography>
                </Stack>
                <Tooltip title="Delete note">
                  <IconButton
                    aria-label={getDeleteNoteAriaLabel(note.text)}
                    onClick={() => setNoteToDelete(note)}
                    size="small"
                    sx={{
                      color: EVOTRUX_ERROR_RED,
                      flexShrink: 0,
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            ))}
          </Stack>
        )}

        <Stack direction="row" justifyContent="flex-start">
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={onOpenModal}
          >
            Add Note
          </Button>
        </Stack>
      </Stack>

      <CreateShipmentNoteModal
        isOpen={isModalOpen}
        onClose={onCloseModal}
        onNoteCreated={handleNoteCreated}
      />

      {noteToDelete && (
        <DeleteShipmentNoteModal
          note={noteToDelete}
          onClose={() => setNoteToDelete(null)}
          onNoteDeleted={refetch}
          onNoteNotFound={refetch}
        />
      )}
    </>
  );
}
